import { setupClass, makeEnum } from '../Base.js'
import { Field, FieldType, ProjectUnits, } from '../Common/Field.js'
import { InputComponent } from '../Common/InputComponent.js'
import { Watcher } from '../Common/Watcher.js'
import { LocalStorageSaver } from '../Common/LocalStorageSaver.js'
import { getGlycolCorrectionFactor } from '../Data/FluidProperties.js'
import { calculateVelocity, autoSizePipe } from '../Data/PipeSizing.js'
import { calcDarcyWeisbachHead } from '../Data/PipeHydraulics.js'

const FluidTypeOptions = makeEnum({
  'Water': 'Water',
  'Propylene-glycol': 'Propylene-glycol',
  'Ethylene-glycol': 'Ethylene-glycol',
})

const PipeMaterialOptions = makeEnum({
  'Steel': 'Steel',
  'Copper': 'Copper',
  'PEX': 'PEX',
})

const PipeSizeOptions = makeEnum({
  'Auto-size': 'Auto-size',
  'Manual size': 'Manual size',
})

/**
 * HydronicsCalculator
 * Calculates hydronic system properties and relationships
 */
export class HydronicsCalculator extends InputComponent {
  init() {
    // We can't use the project watcher here, because the calculator is at
    // the global level.
    this.watcher = Watcher.globalInstance();
    this.updater.setWatcher(this.watcher);
    console.log('[HydronicsCalculator] Initializing calculator');

    this.units = Field.makeSelect('Units', ProjectUnits, { watcher: this.watcher })
    this.units.value = 'Imperial'
    console.log('[HydronicsCalculator] Units field created:', this.units)

    // Create a project context that fields can use to determine display units
    this.projectContext = {
      getProjectUnits: () => this.units.value
    }

    // Heat Transfer Section
    this.heatingCoolingLoad = new Field({
      name: 'Heating/Cooling Load',
      type: FieldType.LoadMBH,
      watcher: this.watcher,
      projectContext: this.projectContext,
      defaultValue: 500,
    })
    console.log('[HydronicsCalculator] HeatingCoolingLoad field created:', this.heatingCoolingLoad)

    this.designTempDiff = new Field({
      name: 'Design Temperature Difference',
      type: FieldType.TempDiffF,
      watcher: this.watcher,
      projectContext: this.projectContext,
      defaultValue: 20,
    })
    console.log('[HydronicsCalculator] DesignTempDiff field created:', this.designTempDiff)

    this.requiredFlowRate = new Field({
      name: 'Required Flow Rate',
      type: FieldType.WaterFlow,
      isOutput: true,
      projectContext: this.projectContext,
      defaultValue: 0,
    })
    console.log('[HydronicsCalculator] RequiredFlowRate field created:', this.requiredFlowRate)

    // Glycol Correction Section - Inputs
    this.fluidType = Field.makeSelect('Fluid Type', FluidTypeOptions, { watcher: this.watcher })
    this.fluidType.value = 'Water'
    console.log('[HydronicsCalculator] Fluid Type field created:', this.fluidType)

    this.glycolConcentration = new Field({
      name: 'Percentage by Volume',
      type: FieldType.Percent,
      watcher: this.watcher,
      projectContext: this.projectContext,
      defaultValue: 0,
    })
    this.glycolConcentration.readOnly = false
    console.log('[HydronicsCalculator] Glycol concentration field created:', this.glycolConcentration)

    this.averageFluidTemp = new Field({
      name: 'Average Fluid Temperature',
      type: FieldType.Temperature,
      watcher: this.watcher,
      projectContext: this.projectContext,
      defaultValue: 60,
    })
    console.log('[HydronicsCalculator] Average fluid temperature field created:', this.averageFluidTemp)

    // Pipe Sizing & Friction Loss Section - Inputs
    this.pipeMaterial = Field.makeSelect('Pipe Material', PipeMaterialOptions, { watcher: this.watcher })
    this.pipeMaterial.value = 'Copper'
    console.log('[HydronicsCalculator] Pipe Material field created:', this.pipeMaterial)

    this.pipeSize = Field.makeSelect('Pipe Size Input', PipeSizeOptions, { watcher: this.watcher })
    this.pipeSize.value = 'Auto-size'
    console.log('[HydronicsCalculator] Pipe Size field created:', this.pipeSize)

    this.pipeSizeValue = new Field({
      name: 'Pipe Size',
      type: FieldType.PipeDiameter,
      watcher: this.watcher,
      projectContext: this.projectContext,
      defaultValue: 1,
    })
    this.pipeSizeValue.setVisibility(() => this.pipeSize.value === 'Manual size')
    console.log('[HydronicsCalculator] Pipe Size Value field created:', this.pipeSizeValue)

    this.pipeLength = new Field({
      name: 'Pipe Length',
      type: FieldType.Length,
      watcher: this.watcher,
      projectContext: this.projectContext,
      defaultValue: 100,
    })
    console.log('[HydronicsCalculator] Pipe Length field created:', this.pipeLength)

    // velocityOutput: no watcher to avoid infinite recursion
    this.velocityOutput = new Field({
      name: 'Velocity',
      type: FieldType.Velocity,
      isOutput: true,
      projectContext: this.projectContext,
      defaultValue: 0,
    })
    console.log('[HydronicsCalculator] Velocity Output field created:', this.velocityOutput)

    // autoSizedPipeName: shows the selected nominal pipe size in auto-size mode
    this.autoSizedPipeName = new Field({
      name: 'Pipe Size',
      type: FieldType.String,
      isOutput: true,
      defaultValue: '—',
    })
    this.autoSizedPipeName.setVisibility(() => this.pipeSize.value === 'Auto-size')

    // Stores the resolved internal diameter (inches) after each calculation;
    // read by the system curve chart to regenerate points reactively.
    this.selectedDiameterIn = { value: 1 }

    // Pump Head Section
    this.fittingsAllowance = new Field({
      name: 'Fittings & Valves Allowance',
      type: FieldType.Percent,
      watcher: this.watcher,
      projectContext: this.projectContext,
      defaultValue: 30,
    })
    console.log('[HydronicsCalculator] Fittings Allowance field created:', this.fittingsAllowance)

    this.reynoldsNumber = new Field({
      name: 'Reynolds Number',
      type: FieldType.Decimal,
      isOutput: true,
      defaultValue: 0,
    })

    this.frictionLossPer100ft = new Field({
      name: 'Friction Loss',
      type: FieldType.FrictionLoss,
      isOutput: true,
      projectContext: this.projectContext,
      defaultValue: 0,
    })

    this.pumpHead = new Field({
      name: 'Total Pump Head',
      type: FieldType.Length,
      isOutput: true,
      projectContext: this.projectContext,
      defaultValue: 0,
    })

    this.watcher.addWatchEffect(() => {
      this.calculateHeatTransfer();
    });

    // Sync the glycolConcentration readOnly flag whenever fluidType changes.
    // Kept as its own watchEffect so it always runs independently of calculation.
    this.fluidType.makeUpdater(() => {
      this.glycolConcentration.readOnly = this.fluidType.value === 'Water';
    });

    // Set up units updater to sync display values when units change
    this.units.makeUpdater((field) => {
      console.log('[HydronicsCalculator] Units updater triggered, new units:', field.value);
      // Resync all field display values to use new units
      this.heatingCoolingLoad._syncDisplayValue();
      this.designTempDiff._syncDisplayValue();
      this.requiredFlowRate._syncDisplayValue();
      this.glycolConcentration._syncDisplayValue();
      this.averageFluidTemp._syncDisplayValue();
      this.pipeSizeValue._syncDisplayValue();
      this.pipeLength._syncDisplayValue();
      this.fittingsAllowance._syncDisplayValue();
      this.velocityOutput._syncDisplayValue();
      this.frictionLossPer100ft._syncDisplayValue();
      this.pumpHead._syncDisplayValue();
    });


    // Set up fields for serialization
    this.serFields = [
      'units',
      'heatingCoolingLoad',
      'designTempDiff',
      'fluidType',
      'glycolConcentration',
      'averageFluidTemp',
      'pipeMaterial',
      'pipeSize',
      'pipeSizeValue',
      'pipeLength',
      'fittingsAllowance',
      'autoSizedPipeName',
      'velocityOutput',
      'reynoldsNumber',
      'frictionLossPer100ft',
      'pumpHead',
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'Hydronics',
      path: 'hydronics',
    }

    // For auto-saving
    this.saver = new LocalStorageSaver('heatwise-hydronics-calculator', this)

    console.log('[HydronicsCalculator] Init complete');
  }

  getProjectUnits() {
    if (this.units.value === 'Metric') {
      return ProjectUnits.Metric
    }
    return ProjectUnits.Imperial
  }

  calculateHeatTransfer() {
    try {
      console.log('[HydronicsCalculator] calculateHeatTransfer() called');
      const load = this.heatingCoolingLoad.value;  // in MBH
      const tempDiff = this.designTempDiff.value;  // in °F

      console.log(`[HydronicsCalculator] Load: ${load} MBH, Temp Diff: ${tempDiff} °F`);

      if (!load || !tempDiff || tempDiff === 0) {
        console.log('[HydronicsCalculator] Skipping calculation due to invalid inputs');
        this.requiredFlowRate.value = 0;
        return;
      }

      // Calculate required flow rate in GPM
      // Formula: Flow (gpm) = Load (Btu/h) / (500 × ΔT (°F))
      // Since Load is in MBH: Flow (gpm) = Load (MBH) × 1000 / (500 × ΔT)
      // Simplified: Flow (gpm) = Load (MBH) × 2 / ΔT
      let flowRate = (load * 1000) / (500 * tempDiff);

      // All field.value properties are always in imperial units.
      const isMetric = this.getProjectUnits() === ProjectUnits.Metric;
      const avgTempF = this.averageFluidTemp.value;  // always °F
      const pipeSizeMode = this.pipeSize.value;
      const material = this.pipeMaterial.value;
      const pipeLengthFt = this.pipeLength.value;    // always ft
      const fittingsAllowanceVal = this.fittingsAllowance.value;

      // Apply glycol correction factor if not using water
      if (this.fluidType.value !== 'Water') {
        const fluidType = this.fluidType.value === 'Propylene-glycol' ? 'propylene' : 'ethylene';
        const concentration = this.glycolConcentration.value;
        const correctionFactor = getGlycolCorrectionFactor(fluidType, avgTempF, concentration);
        console.log(`[HydronicsCalculator] Applying glycol correction factor: ${correctionFactor}`);
        flowRate = flowRate * correctionFactor;
      }

      console.log(`[HydronicsCalculator] Calculated Flow Rate: ${flowRate} gpm`);

      // --- Resolve selected internal diameter in inches ---
      let selectedDiameterIn;
      let autoSizeName = null;
      let velocityFtS;
      if (pipeSizeMode === 'Auto-size') {
        const sizeResult = autoSizePipe(flowRate, material, avgTempF, isMetric);
        autoSizeName = sizeResult.displaySize;
        velocityFtS = sizeResult.velocity;
        selectedDiameterIn = sizeResult.id;
      } else {
        selectedDiameterIn = this.pipeSizeValue.value;  // always inches
        velocityFtS = calculateVelocity(flowRate, selectedDiameterIn);
      }

      // --- Darcy-Weisbach head loss ---
      const { headLoss_ft, reynolds } = calcDarcyWeisbachHead(
        flowRate,
        selectedDiameterIn,
        pipeLengthFt,
        avgTempF,
        material,
      );
      const fittingsFactor = 1 + (fittingsAllowanceVal / 100);
      const totalHead = headLoss_ft * fittingsFactor;
      // Friction loss per 100 ft: guard against zero length.
      const frictionPer100 = pipeLengthFt > 0 ? (headLoss_ft / pipeLengthFt) * 100 : 0;

      this.requiredFlowRate.value = flowRate;
      if (autoSizeName !== null) this.autoSizedPipeName.value = autoSizeName;
      this.velocityOutput.value = velocityFtS;
      this.selectedDiameterIn.value = selectedDiameterIn;
      this.reynoldsNumber.value = Math.round(reynolds);
      this.pumpHead.value = totalHead;
      this.frictionLossPer100ft.value = frictionPer100;
      console.log(`[HydronicsCalculator] Re=${Math.round(reynolds)}, headLoss=${headLoss_ft.toFixed(2)} ft, total=${totalHead.toFixed(2)} ft`);

    } catch (err) {
      console.error('[HydronicsCalculator] Error in calculateHeatTransfer:', err);
    }
  }

  loadFromStorage() {
    this.saver.loadFromStorage()
  }

  async save() {
    await this.saver.save()
  }
}
setupClass(HydronicsCalculator)
